import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';

import type { InboxFilter } from './use-notifications';

// Estado de la vista en la URL (UX-NAV-02): pestaña y filtro de la bandeja. Los valores
// por defecto no se escriben, así el enlace del menú y el de la campana coinciden.

export type NotificationsTab = 'avisos' | 'configuracion';

export function parseTab(raw: string | null): NotificationsTab {
  return raw === 'configuracion' ? 'configuracion' : 'avisos';
}

export function parseFilter(raw: string | null): InboxFilter {
  return raw === 'todos' ? 'todos' : 'abiertos';
}

export interface NotificationsUrlState {
  tab: NotificationsTab;
  filter: InboxFilter;
  setTab: (tab: NotificationsTab) => void;
  setFilter: (filter: InboxFilter) => void;
}

/** `configAllowed`: true/false según el permiso de configuración; null mientras carga
 *  (no se toca la URL para no perder un enlace válido antes de saber). */
export function useNotificationsUrlState(configAllowed: boolean | null): NotificationsUrlState {
  const [params, setParams] = useSearchParams();
  const requested = parseTab(params.get('tab'));
  const denied = requested === 'configuracion' && configAllowed === false;

  const setParam = useCallback(
    (key: string, value: string | null, replace = false) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (value === null) {
            p.delete(key);
          } else {
            p.set(key, value);
          }
          return p;
        },
        { replace },
      ),
    [setParams],
  );

  useEffect(() => {
    if (denied) {
      setParam('tab', null, true);
    }
  }, [denied, setParam]);

  const setTab = useCallback(
    (tab: NotificationsTab) => setParam('tab', tab === 'avisos' ? null : tab),
    [setParam],
  );
  const setFilter = useCallback(
    (filter: InboxFilter) => setParam('estado', filter === 'abiertos' ? null : filter, true),
    [setParam],
  );

  return {
    tab: denied ? 'avisos' : requested,
    filter: parseFilter(params.get('estado')),
    setTab,
    setFilter,
  };
}
